import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, Link, ButtonBase, Grid } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@configurator-messages'
import SubtotalAndAddToCart from './SubtotalAndAddToCart'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../Milliplex'
import { useRouter } from 'next/router'
import { parseParams } from '../Step2/SearchResult/ProductCard/ProductCard'
import {
  getSelectedKitQueryString,
  SelectedKits,
} from '@src/routes/Configurators/products/Milliplex/utils'
import { ListKits } from '@src/types/graphql-types'
import DesignAndPriceModal from '../components/DesignAndPrice/DesignAndPriceModal'
import DeleteModal from '../../../../../components/DeleteModal/DeleteModal'
import { useMilliplexKitsLazyQuery } from '@src/queries/MilliplexKitsQuery.generated'
import SkeletonLoader from './SkeletonLoader'
import ProductCard from './StepThreeProductCard'
import ValidationWarnings from '@src/routes/Configurators/validations/ValidationWarnings'
import AddIcon from '@material-ui/icons/Add'
import LinkStyleButton from '@src/components/LinkStyleButton'
import { getDefaultOrConvertToArray } from '@src/routes/Configurators/utils/util'

const useStyles = makeStyles((theme: Theme) => ({
  listHeader: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightBold,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '23px',
  },
  upperCase: {
    textTransform: 'uppercase',
  },
  deleteAllLink: {
    fontSize: theme.typography.pxToRem(14),
    cursor: 'pointer',
  },
  deleteCloseIcon: {
    fontSize: theme.typography.pxToRem(22),
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightBold,
  },
  tableRow: {
    borderTop: 'none !important',
    height: 'auto !important',
  },
  tableCellContainer: {
    padding: '0px 16px 21.5px 16px',
  },
  fontRegular: {
    fontWeight: 700,
  },
  itemPadding: {
    padding: '4px 0px !important',
  },
  itemTopMargin: {
    marginTop: theme.spacing(2),
  },
  productStyle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.typography.fontWeightBold,
    //textWrap: 'nowrap',
  },
  alignRight: {
    textAlign: 'right',
    textWrap: 'nowrap',
  },
  analytesInKitStyle: {
    paddingBlock: '0px 4px',
    width: '100%',
  },
  kitStyle: {
    minWidth: '17.5%',
  },
  flexBasisStyle: {
    flexBasis: '23.5%',
  },
  customPremixStyle: {
    padding: '4.5px 0px !important',
  },
  customPremixMargin: {
    marginLeft: theme.spacing(2.25),
  },
  kitPadding: {
    paddingRight: '10px',
  },
  warningMessage: {
    marginBottom: '24px',
  },
}))

const MilliplexStep3: React.FC<{
  setDisableSearchCriteria: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ setDisableSearchCriteria }) => {
  const formik = useFormikContext<MilliplexFormValues>()
  const router = useRouter()
  const classes = useStyles()
  const intl = useIntl()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteType, setDeleteType] = useState('')
  const [deletedItemId, setDeletedItemId] = useState('')

  const [sharedSelectedKitData, setSharedSelectedKitData] =
    useState<SelectedKits>({})

  const panelIdList: string[] = []
  const kitsUrlList: string[] = router.query.selectedKits
    ? JSON.parse(router.query.selectedKits as string)
    : []

  kitsUrlList.forEach((item: string) => panelIdList.push(item.split('_')[0]))

  const parsedSelectedKitListInfo = useMemo(() => {
    return parseParams(
      kitsUrlList.reduce(
        (acc, url, index) => {
          acc[index.toString()] = url
          return acc
        },
        {} as { [key: string]: string }
      )
    )
  }, [kitsUrlList])

  const [fetchMilliplexKits, { loading }] = useMilliplexKitsLazyQuery({
    variables: {
      input: {
        page: 1,
        species: router.query.species,
        researchAreas: getDefaultOrConvertToArray(router?.query?.researchAreas),
        analytes: getDefaultOrConvertToArray(router?.query?.analytes),
        panelId: null,
        count: 25,
        panelIdList: parsedSelectedKitListInfo.map((kit) => ({
          panelId: kit.panelId,
          analytes: kit.analytes,
          customPremixSelection: kit.customPremixing,
        })),
      },
    },
    onCompleted: (data) => {
      const newKitsFromUrl = parsedSelectedKitListInfo
        .map((milliplexKit) => {
          const {
            sampleType,
            spaceSaver,
            customPremixing,
            quantity,
            analytes: selectedAnalytes,
            isPremixed,
            kitInstrument,
          } = milliplexKit
          const isPremixedKit = isPremixed === 'true'

          const kitFromApi = data?.getMilliplexKits?.listKits.find(
            (kit) => kit.panelId === milliplexKit.panelId
          )
          if (!kitFromApi) return {}
          return {
            [milliplexKit?.panelId]: {
              url: isPremixedKit
                ? `${milliplexKit.panelId}_${isPremixedKit}_____${quantity}`
                : `${
                    milliplexKit.panelId
                  }_${isPremixedKit}_${selectedAnalytes.join(
                    ','
                  )}_${sampleType}_${spaceSaver}_${customPremixing}_${quantity}_${kitInstrument}`,
              data: {
                productNo: milliplexKit.panelId,
                productName: kitFromApi?.panelName,
                price: kitFromApi?.price || '',
                analytesInKit: isPremixedKit
                  ? kitFromApi?.analytes
                  : selectedAnalytes,
                sampleType,
                customPremixing,
                spaceSaving: spaceSaver,
                quantity,
                currency: kitFromApi?.currency || '',
                milliplexKitDataFromApi: kitFromApi,
              },
            },
          }
        })
        .reduce((acc, item) => {
          const productNo = Object.keys(item)[0]
          acc[productNo] = item[productNo]
          return acc
        }, {})
      setSharedSelectedKitData(newKitsFromUrl)
    },
  })

  useEffect(() => {
    if (
      panelIdList.length > 0 &&
      Object.keys(formik.values.selectedKits).length === 0
    ) {
      fetchMilliplexKits()
    }
  }, [panelIdList.length])

  const memoizedSharedSelectedKitData = useMemo(() => {
    return sharedSelectedKitData
  }, [sharedSelectedKitData])
  useEffect(() => {
    Object.keys(sharedSelectedKitData).length > 0 &&
      formik.setFieldValue('selectedKits', sharedSelectedKitData)
  }, [memoizedSharedSelectedKitData])

  const selectedKits = formik.values.selectedKits

  const productDetails = Object.values(selectedKits).map((kit) => kit.data)

  const displayWarning = productDetails.find((kit) => {
    return kit.price === '' || kit.price === undefined
  })

  const [isDesignAndPriceModalOpen, setDesignAndPriceModal] =
    useState<boolean>(false)

  const [milliplexKit, setMilliplexKit] = useState<ListKits>()

  const handleQuantityChange = (quantity: number, id: string) => {
    const kitDetails = selectedKits[id]
    if (kitDetails) {
      const {
        panelId,
        isPremixed,
        analytes,
        sampleType,
        spaceSaver,
        customPremixing,
        kitInstrument,
      } = parseParams({
        [id]: kitDetails.url,
      })[0]
      const newSelectedKits = {
        ...selectedKits,
        [id]: {
          url: `${panelId}_${isPremixed}_${analytes.join(
            ','
          )}_${sampleType}_${spaceSaver}_${customPremixing}_${quantity}_${kitInstrument}`,
          data: { ...kitDetails.data, quantity },
        },
      }
      formik.setFieldValue('selectedKits', newSelectedKits)
      const urlString = getSelectedKitQueryString(newSelectedKits)
      const currentQueryParams = { ...router.query, selectedKits: urlString }
      router.replace(
        {
          pathname: router.pathname,
          query: currentQueryParams,
        },
        undefined,
        { shallow: true, scroll: false }
      )
    }
  }

  const openDeleteDialog = (type: 'Single' | 'All', itemId?: string) => {
    setDeleteType(type)

    if (type === 'Single' && itemId) {
      setDeletedItemId(itemId)
    }

    setDialogOpen(true)
  }
  const handleDeleteItem = (itemId: string) => {
    openDeleteDialog('Single', itemId)
  }

  const handleDeleteAll = () => {
    openDeleteDialog('All')
  }

  const handleAddAnotherKit = () => {
    formik.setFieldValue('species', '')
    formik.setFieldTouched('species', false)
    setDisableSearchCriteria(false)
    const { country, language } = router.query
    router.replace({
      pathname: router.pathname,
      query: {
        country,
        language,
        activeLink: '',
      },
    })
  }

  const handleDeleteAllAction = () => {
    formik.resetForm()
    const { country, language } = router.query
    router.replace({
      pathname: router.pathname,
      query: {
        country,
        language,
        activeLink: '',
      },
    })
    setDisableSearchCriteria(false)
  }

  const handleDeleteSingleAction = (itemId: string) => {
    const { [itemId]: _deletedItem, ...newObject } = formik.values.selectedKits

    formik.setFieldValue('selectedKits', newObject)
    const urlString = getSelectedKitQueryString(newObject)
    const currentQueryParams = { ...router.query, selectedKits: urlString }
    router.replace(
      {
        pathname: router.pathname,
        query: currentQueryParams,
      },
      undefined,
      { shallow: true, scroll: false }
    )

    if (Object.keys(newObject).length === 0) {
      handleDeleteAllAction()
    }
  }

  const closeDeleteDialog = () => {
    setDialogOpen(false)
    setDeleteType('')
  }

  const handleDelete = (id?: string) => {
    if (deleteType === 'All') {
      handleDeleteAllAction()
    } else if (deleteType === 'Single' && id) {
      handleDeleteSingleAction(id)
    }
    closeDeleteDialog()
  }

  const handleEditButton = (id: string) => {
    setMilliplexKit(selectedKits[id]?.data?.milliplexKitDataFromApi)
    setDesignAndPriceModal(true)
  }

  return loading ? (
    <SkeletonLoader />
  ) : (
    <>
      {displayWarning ? (
        <div className={classes.warningMessage}>
          <ValidationWarnings
            warnings={[intl.formatMessage(messages.PC_PRICE_ERROR_EMPTY_PRICE)]}
          />
        </div>
      ) : (
        ''
      )}
      <div className={classes.listHeader}>
        <span className={classes.upperCase}>
          <FormattedMessage {...messages.CONF_CONFIRM_ORDER_milliplex} />
        </span>
        <ButtonBase onClick={handleDeleteAll}>
          <Link className={classes.deleteAllLink}>
            <FormattedMessage {...messages.DELETE_ALL} />
          </Link>
        </ButtonBase>
      </div>
      {productDetails.map((detail) => (
        <ProductCard
          productData={detail}
          handleQuantityChange={handleQuantityChange}
          handleEdit={handleEditButton}
          handleDeleteItem={handleDeleteItem}
          key={detail.productNo}
        />
      ))}
      {isDesignAndPriceModalOpen && milliplexKit && (
        <DesignAndPriceModal
          open={isDesignAndPriceModalOpen}
          handleClose={() => setDesignAndPriceModal(false)}
          milliplexKit={milliplexKit}
        />
      )}

      <Grid container justifyContent="flex-end">
        <LinkStyleButton onClick={handleAddAnotherKit}>
          <AddIcon />
          <FormattedMessage
            id="ADD_ANOTHER_KIT"
            defaultMessage="Add Another Kit"
          />
        </LinkStyleButton>
      </Grid>

      {!loading && productDetails.length > 0 && <SubtotalAndAddToCart />}
      <DeleteModal
        dialogOpen={dialogOpen}
        handleClose={closeDeleteDialog}
        handleDelete={() => handleDelete(deletedItemId)}
        deleteType={deleteType}
      />
    </>
  )
}

export default MilliplexStep3
